import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, TouchableWithoutFeedback, Platform, ScrollView, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import * as DocumentPicker from 'expo-document-picker';

import FormInput from '../components/FormInput';
import ToggleSwitch from '../components/ToggleSwitch';
import SettingsSidebar from '../components/SettingsSidebar';
import PrimaryButton from '../components/PrimaryButton';
import Modal from '../components/Modal';
import { calendarSchedulingService, TRACKABLE_EVENTS } from '../services/calendarSchedulingService';

const ProfileScreen = () => {
  const { palette, mode } = useTheme();
  const { user, logout, uploadProfilePhoto, removeProfilePhoto, deleteAccount, updateUserPreferences, saveUserProfile } = useAuth();
  const navigation = useNavigation();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  // Modal states
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [logoutModalVisible, setLogoutModalVisible] = useState(false);

  const [deleteAccountModalVisible, setDeleteAccountModalVisible] = useState(false);

  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const [photoMenuVisible, setPhotoMenuVisible] = useState(false);
  const photoMenuRef = useRef(null);
  const photoMenuDropdownRef = useRef(null);

  // Active section state
  const [activeSection, setActiveSection] = useState('account');

  // Preferences state
  const [preferences, setPreferences] = useState({
    darkMode: true,
    emailNotifications: true,
    autoUpdate: true,
    showGlossary: true,
    notifications: {
      registrationEmails: true,
      progressReports: true,
      frequency: 'weekly', // daily, weekly, monthly, disabled
    },
    notifications: {
      progressReports: true,
      frequency: 'weekly',
      time: '05:00',
      timezone: 'America/Los_Angeles',
      registrationEmails: true
    }
  });

  // Schedule preferences state
  const [schedulePreferences, setSchedulePreferences] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);


  // Load user data on component mount
  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
      setProfilePhoto(user.profilePhoto || user.photoURL || '');

      // Load preferences with defaults
      const defaultPreferences = {
        darkMode: true,
        emailNotifications: true,
        autoUpdate: true,
        showGlossary: true,
        notifications: {
          progressReports: true,
          frequency: 'weekly',
          time: '05:00',
          timezone: 'America/Los_Angeles',
          registrationEmails: true
        }
      };
      setPreferences(user.preferences || defaultPreferences);
    }
  }, [user]);

  // Load schedule preferences when schedule section is active
  useEffect(() => {
    if (activeSection === 'schedule' && user?.uid && !schedulePreferences) {
      loadSchedulePreferences();
    }
  }, [activeSection, user?.uid]);

  const loadSchedulePreferences = async () => {
    setLoadingSchedule(true);
    const { preferences: prefs } = await calendarSchedulingService.getUserSchedulePreferences(user.uid);
    setSchedulePreferences(prefs || calendarSchedulingService.getDefaultPreferences());
    setLoadingSchedule(false);
  };

  const handleScheduleToggle = async (path) => {
    const newPrefs = { ...schedulePreferences };
    const keys = path.split('.');
    
    // Navigate to the nested value
    let current = newPrefs;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = !current[keys[keys.length - 1]];
    
    setSchedulePreferences(newPrefs);
  };

  const handleScheduleTimeChange = (path, value) => {
    const newPrefs = { ...schedulePreferences };
    const keys = path.split('.');
    
    let current = newPrefs;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setSchedulePreferences(newPrefs);
  };

  const handleDayOfWeekChange = (day) => {
    setSchedulePreferences(prev => ({
      ...prev,
      weeklyActions: {
        ...prev.weeklyActions,
        dayOfWeek: day,
      }
    }));
  };

  const saveScheduleSettings = async () => {
    setSavingSchedule(true);
    const { success, error } = await calendarSchedulingService.saveSchedulePreferences(user.uid, schedulePreferences);
    setSavingSchedule(false);
    
    if (success) {
      setSuccessModalVisible(true);
    } else {
      showErrorModal(error || 'Failed to save schedule settings');
    }
  };


  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  const onLogout = () => {
    setLogoutModalVisible(true);
  };

  const handleLogoutConfirm = async () => {
    const { error } = await logout();
    setLogoutModalVisible(false);
    if (!error) {
      navigation.navigateTo('Login');
    } else {
      showErrorModal('Failed to logout. Please try again.');
    }
  };

  const handlePhotoUpload = async () => {
    if (!user?.uid) {
      showErrorModal('Please log in again');
      return;
    }

    setPhotoMenuVisible(false);
    setBusy(true);

    try {
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        const response = await fetch(fileUri);
        const fileBlob = await response.blob();

        const fileExt = file.name.split('.').pop();
        const fileName = `${user.uid}.${fileExt}`;

        const uploadResult = await uploadProfilePhoto(fileBlob, fileName);

        if (uploadResult.success) {
          // Photo URL should be updated via AuthContext state
          // But we also set local state to ensure immediate UI update
          setProfilePhoto(uploadResult.photoURL);
        } else {
          console.error('[ProfilePage] Upload failed:', uploadResult.error);
          showErrorModal('Failed to update profile photo. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      showErrorModal('Failed to update profile photo. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleEditPhotoClick = () => {
    if (profilePhoto) {
      // Show dropdown menu if photo exists
      setPhotoMenuVisible(true);
    } else {
      // Directly open file picker if no photo
      handlePhotoUpload();
    }
  };

  const handleDeletePhoto = async () => {
    setPhotoMenuVisible(false);
    await onRemovePhoto();
  };

  const onRemovePhoto = async () => {
    setBusy(true);
    try {
      await removeProfilePhoto();
      setProfilePhoto('');
    } finally {
      setBusy(false);
    }
  };

  const onDeleteAccount = () => {
    setDeleteAccountModalVisible(true);
  };

  const handleDeleteAccountConfirm = async () => {
    setBusy(true);
    setDeleteAccountModalVisible(false);
    try {
      await deleteAccount();
    } catch (error) {
      showErrorModal('Failed to delete account. Please try again.');
      setBusy(false);
    }
  };

  // Handle preference toggle
  const handlePreferenceToggle = async (prefKey) => {
    const newPreferences = {
      ...preferences,
      [prefKey]: !preferences[prefKey],
    };
    setPreferences(newPreferences);

    // Save to backend
    const result = await updateUserPreferences(newPreferences);
    if (!result.success) {
      // Revert on error
      setPreferences(preferences);
      showErrorModal(`Failed to update preference: ${result.error}`);
    }
  };

  // Handle nested notification preference toggle
  const handleNotificationToggle = async (notifKey) => {
    const newPreferences = {
      ...preferences,
      notifications: {
        ...preferences.notifications,
        [notifKey]: !preferences.notifications?.[notifKey],
      }
    };
    setPreferences(newPreferences);

    // Save to backend
    const result = await updateUserPreferences(newPreferences);
    if (!result.success) {
      // Revert on error
      setPreferences(preferences);
      showErrorModal(`Failed to update notification preference: ${result.error}`);
    }
  };

  // Handle notification frequency change
  const handleFrequencyChange = async (frequency) => {
    const newPreferences = {
      ...preferences,
      notifications: {
        ...preferences.notifications,
        frequency: frequency,
      }
    };
    setPreferences(newPreferences);

    // Save to backend
    const result = await updateUserPreferences(newPreferences);
    if (!result.success) {
      // Revert on error
      setPreferences(preferences);
      showErrorModal(`Failed to update frequency preference: ${result.error}`);
    }
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!user?.uid) {
      showErrorModal('Please log in again');
      return;
    }

    // Validate required fields
    if (!username.trim()) {
      showErrorModal('Username is required');
      return;
    }

    if (!firstName.trim()) {
      showErrorModal('First name is required');
      return;
    }

    if (!lastName.trim()) {
      showErrorModal('Last name is required');
      return;
    }

    setSaving(true);

    try {
      const updatedProfile = {
        username: username.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        updatedAt: new Date(),
      };

      const result = await saveUserProfile(updatedProfile);

      if (result.success) {
        // Show success message
        setSuccessModalVisible(true);
      } else {
        showErrorModal(result.error || 'Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error saving profile changes:', error);
      showErrorModal('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Error Modal
  const ErrorModal = () => (
    <Modal
      visible={errorModalVisible}
      onClose={() => setErrorModalVisible(false)}
      title="Error"
      showCloseButton={true}
    >
      <View style={styles.modalContentWrapper}>
        <Text style={[styles.modalMessage, { color: palette.text || 'white' }]}>
          {errorMessage}
        </Text>
        <View style={styles.modalActions}>
          <View style={styles.modalSingleAction}>
            <PrimaryButton
              title="OK"
              onPress={() => setErrorModalVisible(false)}
              style={styles.modalPrimaryButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );

  // Success Modal
  const SuccessModal = () => (
    <Modal
      visible={successModalVisible}
      onClose={() => setSuccessModalVisible(false)}
      title="Success"
      showCloseButton={true}
    >
      <View style={styles.modalContentWrapper}>
        <Text style={[styles.modalMessage, { color: palette.text || 'white' }]}>
          Your profile has been updated successfully!
        </Text>
        <View style={styles.modalActions}>
          <View style={styles.modalSingleAction}>
            <PrimaryButton
              title="OK"
              onPress={() => setSuccessModalVisible(false)}
              style={styles.modalPrimaryButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );

  // Logout Confirmation Modal
  const LogoutModal = () => (
    <Modal
      visible={logoutModalVisible}
      onClose={() => setLogoutModalVisible(false)}
      title="Logout"
      showCloseButton={false}
    >
      <View style={styles.modalContentWrapper}>
        <Text style={[styles.modalMessage, { color: palette.text || 'white' }]}>
          Are you sure you want to logout?
        </Text>
        <View style={styles.modalActions}>
          <View style={styles.modalDualActions}>
            <TouchableOpacity
              onPress={() => setLogoutModalVisible(false)}
              style={[styles.modalCancelButton, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}
              disabled={busy}
            >
              <Text style={[styles.modalCancelButtonText, { color: palette.text || 'white' }]}>
                Cancel
              </Text>
            </TouchableOpacity>
            <PrimaryButton
              title="Logout"
              onPress={handleLogoutConfirm}
              disabled={busy}
              style={styles.modalPrimaryButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );

  // Delete Account Confirmation Modal
  const DeleteAccountModal = () => {
    const [deleteConfirmText, setDeleteConfirmText] = useState('');


    return (
    <Modal
      visible={deleteAccountModalVisible}
      onClose={() => setDeleteAccountModalVisible(false)}
      title="Delete Account"
      showCloseButton={false}
    >
      <View style={styles.modalContentWrapper}>
        <Text style={[styles.modalMessage, { color: palette.text || 'white' }]}>
          Delete account permanently? This cannot be undone.
        </Text>
        <Text style={[styles.modalMessage, { color: palette.text || 'white' }]}>Type "delete account" to confirm</Text>
        <FormInput
          placeholder="Type 'delete account' to confirm"
          value={deleteConfirmText}
          onChangeText={setDeleteConfirmText}
          editable={!busy}
        />
        <View style={styles.modalActions}>
          <View style={styles.modalDualActions}>
            <TouchableOpacity
              onPress={() => setDeleteAccountModalVisible(false)}
              style={[styles.modalCancelButton, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}
              disabled={busy}
            >
              <Text style={[styles.modalCancelButtonText, { color: palette.text || 'white' }]}>
                Cancel
              </Text>
            </TouchableOpacity>
            <PrimaryButton
              title="Delete"
              onPress={handleDeleteAccountConfirm}
              disabled={busy || deleteConfirmText !== 'delete account'}
              style={[styles.modalPrimaryButton, styles.modalDeleteButton]}
            />
          </View>
        </View>
      </View>
    </Modal>
  )};


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideButton = photoMenuRef.current && photoMenuRef.current.contains(event.target);
      const clickedInsideMenu = photoMenuDropdownRef.current && photoMenuDropdownRef.current.contains(event.target);
      
      if (!clickedInsideButton && !clickedInsideMenu) {
        setPhotoMenuVisible(false);
      }
    };

    if (photoMenuVisible) {
      // Add event listener for web
      if (typeof window !== 'undefined') {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }
  }, [photoMenuVisible]);

  return (
    <View style={styles.container}>
      <ErrorModal />
      <SuccessModal />
      <LogoutModal />
      <DeleteAccountModal />

      <View style={styles.layoutRow}>
        {/* Sidebar */}
        <SettingsSidebar currentPage={activeSection} onPageChange={setActiveSection} />

        {/* Main content */}
        <View style={styles.mainCol}>
          <View style={styles.headerBlock}>
            <Text style={[styles.headerTitle, { color: palette.text || 'white' }]}>Settings</Text>
            <View style={styles.profileEditRow}>
              <View style={styles.avatarWrap}>
                {profilePhoto ? (
                  <Image
                    source={{ uri: profilePhoto }}
                    style={styles.bigAvatar}
                  />
                ) : (
                  <View style={[styles.bigAvatar, styles.avatarPlaceholder, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                    <MaterialCommunityIcons name="account-outline" size={42} color={palette.mutedText || 'rgba(255,255,255,0.60)'} />
                  </View>
                )}
                <View style={styles.avatarEditBtnContainer} ref={photoMenuRef}>
                    <TouchableOpacity
                      onPress={handleEditPhotoClick}
                      disabled={busy}
                      style={[styles.avatarEditBtn, { backgroundColor: palette.primary }]}
                    >
                      <Text style={{ color: '#000' }}>✎</Text>
                    </TouchableOpacity>
                </View>
                {photoMenuVisible && profilePhoto && (
                  <View 
                    ref={photoMenuDropdownRef}
                    style={[styles.photoMenu, {
                      backgroundColor: palette.surfaceSolid,
                      borderColor: palette.border,
                    }]}
                  >
                    <TouchableOpacity
                      onPress={handlePhotoUpload}
                      disabled={busy}
                      style={styles.photoMenuItem}
                    >
                      <Text style={[styles.photoMenuText, { color: palette.text }]}>Edit Photo</Text>
                    </TouchableOpacity>
                    <View style={[styles.photoMenuDivider, { backgroundColor: palette.border }]} />
                    <TouchableOpacity
                      onPress={handleDeletePhoto}
                      disabled={busy}
                      style={styles.photoMenuItem}
                    >
                      <Text style={[styles.photoMenuText, { color: '#EA806B' }]}>Delete Photo</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              <View style={styles.usernameCol}>
                <Text style={[styles.label, { color: palette.text || 'white' }]}>Username</Text>
                <View style={[styles.outlineField, { borderColor: palette.border || 'white' }]}>
                  <FormInput
                    placeholder="Enter your username"
                    value={username}
                    onChangeText={setUsername}
                    editable={!saving && !busy}
                  />
                </View>
              </View>
            </View>
          </View>

          {/* Account Information Section */}
          {activeSection === 'account' && (
          <View style={[styles.sectionBlock, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
            <Text style={[styles.sectionHeader, { color: palette.text || 'white' }]}>Account Information</Text>
            <View style={styles.inlineRow}>
              <View style={styles.inlineCol}>
                <Text style={[styles.smallLabel, { color: palette.text || 'white' }]}>First Name</Text>
                <View style={[styles.outlineField, { borderColor: palette.border || 'white' }]}>
                  <FormInput placeholder="Enter first name" value={firstName} onChangeText={setFirstName} editable={!saving && !busy} />
                </View>
              </View>
              <View style={styles.inlineCol}>
                <Text style={[styles.smallLabel, { color: palette.text || 'white' }]}>Last Name</Text>
                <View style={[styles.outlineField, { borderColor: palette.border || 'white' }]}>
                  <FormInput placeholder="Enter last name" value={lastName} onChangeText={setLastName} editable={!saving && !busy} />
                </View>
              </View>
            </View>

            <View style={styles.actionsRow}>
              <TouchableOpacity
                onPress={handleSaveChanges}
                disabled={saving || busy}
                style={[
                  styles.outlineBtn,
                  {
                    borderColor: palette.border || 'rgba(255,255,255,0.16)',
                    opacity: (saving || busy) ? 0.5 : 1
                  }
                ]}
              >
                <Text style={[styles.outlineBtnText, { color: palette.text || 'white' }]}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  // Reset to original values
                  if (user) {
                    setUsername(user.username || '');
                    setFirstName(user.firstName || '');
                    setLastName(user.lastName || '');
                  }
                }}
                disabled={saving || busy}
                style={[
                  styles.outlineBtn,
                  {
                    borderColor: palette.border || 'rgba(255,255,255,0.16)',
                    opacity: (saving || busy) ? 0.5 : 1
                  }
                ]}
              >
                <Text style={[styles.outlineBtnText, { color: palette.text || 'white' }]}>Cancel</Text>
              </TouchableOpacity>
              <View style={{ flex: 1 }} />
              <TouchableOpacity onPress={onLogout} style={[styles.outlineBtn, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}><Text style={[styles.outlineBtnText, { color: palette.text || 'white' }]}>Logout</Text></TouchableOpacity>
              <TouchableOpacity onPress={onDeleteAccount} disabled={busy} style={[styles.outlineBtn, { borderColor: '#EA806B' }]}><Text style={[styles.outlineBtnText, { color: '#EA806B' }]}>Delete Account</Text></TouchableOpacity>
            </View>
          </View>
          )}

          {/* Preferences Section */}
          {activeSection === 'preferences' && (
            <View style={[styles.sectionBlock, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
              <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>General Preferences</Text>
              {[
                { key: 'darkMode', title: 'Dark Mode', desc: 'Enable dark theme' },
                { key: 'emailNotifications', title: 'Email Notifications', desc: 'Receive updates via email' },
                { key: 'autoUpdate', title: 'Auto-Update', desc: 'Automatically switch theme based on time (dark 5PM-5AM, light 5AM-5PM)' },
                { key: 'showGlossary', title: 'Show Glossary', desc: 'Display the Business Glossary panel on dashboard and course pages' },
              ].map((p) => {
                const isOn = preferences[p.key];
                return (
                  <View key={p.key} style={styles.toggleRow}>
                    <View style={styles.toggleInfo}>
                      <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>{p.title}</Text>
                      <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>{p.desc}</Text>
                    </View>
                    <ToggleSwitch
                      isOn={isOn}
                      onToggle={() => handlePreferenceToggle(p.key)}
                      disabled={busy}
                    />
                  </View>
                );
              })}
              
              {/* Notification Settings */}
              <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginTop: 40 }]}>Notification Settings</Text>
              
              {/* Progress Reports Toggle */}
              <View style={styles.toggleRow}>
                <View style={styles.toggleInfo}>
                  <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Progress Reports</Text>
                  <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Receive regular progress updates via email</Text>
                </View>
                <ToggleSwitch
                  isOn={preferences.notifications?.progressReports || false}
                  onToggle={() => handleNotificationToggle('progressReports')}
                  disabled={busy}
                />
              </View>
              
              {/* Frequency Selector - Only show if progress reports are enabled */}
              {preferences.notifications?.progressReports && (
                <View style={styles.toggleRow}>
                  <View style={styles.toggleInfo}>
                    <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Report Frequency</Text>
                    <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>How often you receive progress reports</Text>
                  </View>
                  <View style={styles.frequencyButtons}>
                    {['daily', 'weekly', 'monthly'].map((freq) => (
                      <TouchableOpacity
                        key={freq}
                        onPress={() => handleFrequencyChange(freq)}
                        style={[
                          styles.frequencyButton,
                          {
                            backgroundColor: preferences.notifications?.frequency === freq ? 
                              palette.primary || '#667eea' : 
                              'transparent',
                            borderColor: palette.border || 'rgba(255,255,255,0.16)'
                          }
                        ]}
                        disabled={busy}
                      >
                        <Text style={[
                          styles.frequencyButtonText,
                          {
                            color: preferences.notifications?.frequency === freq ?
                              'white' :
                              palette.text || 'rgba(255, 255, 255, 0.80)'
                          }
                        ]}>
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}
              
              {/* Registration Emails Toggle */}
              <View style={styles.toggleRow}>
                <View style={styles.toggleInfo}>
                  <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Welcome Emails</Text>
                  <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Receive welcome email when you register</Text>
                </View>
                <ToggleSwitch
                  isOn={preferences.notifications?.registrationEmails || true}
                  onToggle={() => handleNotificationToggle('registrationEmails')}
                  disabled={busy}
                />
              </View>
              
              {/* Email Schedule Info */}
              {preferences.notifications?.progressReports && (
                <View style={[styles.infoBox, { backgroundColor: palette.surface || 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                  <MaterialCommunityIcons name="information-outline" size={20} color={palette.primary || '#667eea'} />
                  <Text style={[styles.infoText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                    Progress reports are sent every {preferences.notifications?.frequency || 'weekly'} at 5:00 AM PST
                    {preferences.notifications?.frequency === 'weekly' && ' on Mondays'}
                    {preferences.notifications?.frequency === 'monthly' && ' on the first Monday of each month'}
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* Schedule Settings Section */}
          {activeSection === 'schedule' && (
            <View style={[styles.sectionBlock, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
              {loadingSchedule ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color={palette.primary || '#FF4747'} />
                  <Text style={[styles.loadingText, { color: palette.mutedText }]}>Loading schedule settings...</Text>
                </View>
              ) : schedulePreferences ? (
                <>
                  {/* Master Schedule Toggle */}
                  <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Calendar Scheduling</Text>
                  <View style={styles.toggleRow}>
                    <View style={styles.toggleInfo}>
                      <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Enable Auto-Scheduling</Text>
                      <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Automatically add tasks to your calendar</Text>
                    </View>
                    <ToggleSwitch
                      isOn={schedulePreferences.enabled}
                      onToggle={() => handleScheduleToggle('enabled')}
                      disabled={savingSchedule}
                    />
                  </View>

                  {schedulePreferences.enabled && (
                    <>
                      {/* Daily Decision */}
                      <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginTop: 32 }]}>
                        <Text style={{ fontSize: 20 }}>🎯</Text> Daily Decision
                      </Text>
                      <View style={styles.toggleRow}>
                        <View style={styles.toggleInfo}>
                          <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Daily Decision Reminders</Text>
                          <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Add daily decision tasks to your calendar</Text>
                        </View>
                        <ToggleSwitch
                          isOn={schedulePreferences.dailyDecision?.enabled}
                          onToggle={() => handleScheduleToggle('dailyDecision.enabled')}
                          disabled={savingSchedule}
                        />
                      </View>
                      {schedulePreferences.dailyDecision?.enabled && (
                        <View style={styles.timePickerRow}>
                          <Text style={[styles.timeLabel, { color: palette.text }]}>Preferred Time:</Text>
                          <View style={styles.timeButtons}>
                            {['09:00', '10:00', '12:00', '14:00'].map((time) => (
                              <TouchableOpacity
                                key={time}
                                onPress={() => handleScheduleTimeChange('dailyDecision.time', time)}
                                style={[
                                  styles.timeButton,
                                  {
                                    backgroundColor: schedulePreferences.dailyDecision?.time === time ? 
                                      (palette.primary || '#FF4747') : 'transparent',
                                    borderColor: palette.border || 'rgba(255,255,255,0.16)'
                                  }
                                ]}
                              >
                                <Text style={[
                                  styles.timeButtonText,
                                  { color: schedulePreferences.dailyDecision?.time === time ? 'white' : palette.text }
                                ]}>
                                  {time}
                                </Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>
                      )}

                      {/* V12 Diagnostic */}
                      <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginTop: 32 }]}>
                        <Text style={{ fontSize: 20 }}>🏎️</Text> V12 Diagnostic
                      </Text>
                      <View style={styles.toggleRow}>
                        <View style={styles.toggleInfo}>
                          <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Monthly Diagnostic Reminders</Text>
                          <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Add V12 diagnostic tasks to your calendar (1st of each month)</Text>
                        </View>
                        <ToggleSwitch
                          isOn={schedulePreferences.v12Diagnostic?.enabled}
                          onToggle={() => handleScheduleToggle('v12Diagnostic.enabled')}
                          disabled={savingSchedule}
                        />
                      </View>

                      {/* Weekly Actions */}
                      <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginTop: 32 }]}>Weekly Actions</Text>
                      <View style={styles.toggleRow}>
                        <View style={styles.toggleInfo}>
                          <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Enable Weekly Actions</Text>
                          <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Schedule Tiger Team, Strategic Action & Decision tasks</Text>
                        </View>
                        <ToggleSwitch
                          isOn={schedulePreferences.weeklyActions?.enabled}
                          onToggle={() => handleScheduleToggle('weeklyActions.enabled')}
                          disabled={savingSchedule}
                        />
                      </View>

                      {schedulePreferences.weeklyActions?.enabled && (
                        <>
                          {/* Day of Week Selector */}
                          <View style={styles.dayOfWeekRow}>
                            <Text style={[styles.timeLabel, { color: palette.text }]}>Weekly Day:</Text>
                            <View style={styles.dayButtons}>
                              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                                <TouchableOpacity
                                  key={day}
                                  onPress={() => handleDayOfWeekChange(index)}
                                  style={[
                                    styles.dayButton,
                                    {
                                      backgroundColor: schedulePreferences.weeklyActions?.dayOfWeek === index ? 
                                        (palette.primary || '#FF4747') : 'transparent',
                                      borderColor: palette.border || 'rgba(255,255,255,0.16)'
                                    }
                                  ]}
                                >
                                  <Text style={[
                                    styles.dayButtonText,
                                    { color: schedulePreferences.weeklyActions?.dayOfWeek === index ? 'white' : palette.text }
                                  ]}>
                                    {day}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>

                          {/* Individual Weekly Actions */}
                          <View style={[styles.weeklyActionsCard, { backgroundColor: palette.surface || 'rgba(255,255,255,0.05)', borderColor: palette.border }]}>
                            {/* Tiger Team */}
                            <View style={styles.weeklyActionItem}>
                              <Text style={[styles.weeklyActionIcon]}>🐅</Text>
                              <View style={styles.weeklyActionInfo}>
                                <Text style={[styles.weeklyActionTitle, { color: palette.text }]}>Tiger Team Breakouts</Text>
                              </View>
                              <ToggleSwitch
                                isOn={schedulePreferences.weeklyActions?.tigerTeamBreakouts?.enabled}
                                onToggle={() => handleScheduleToggle('weeklyActions.tigerTeamBreakouts.enabled')}
                                disabled={savingSchedule}
                              />
                            </View>

                            {/* Strategic Action */}
                            <View style={styles.weeklyActionItem}>
                              <Text style={[styles.weeklyActionIcon]}>⚡</Text>
                              <View style={styles.weeklyActionInfo}>
                                <Text style={[styles.weeklyActionTitle, { color: palette.text }]}>Strategic Action Matrix</Text>
                              </View>
                              <ToggleSwitch
                                isOn={schedulePreferences.weeklyActions?.strategicAction?.enabled}
                                onToggle={() => handleScheduleToggle('weeklyActions.strategicAction.enabled')}
                                disabled={savingSchedule}
                              />
                            </View>

                            {/* Strategic Decision */}
                            <View style={styles.weeklyActionItem}>
                              <Text style={[styles.weeklyActionIcon]}>🎲</Text>
                              <View style={styles.weeklyActionInfo}>
                                <Text style={[styles.weeklyActionTitle, { color: palette.text }]}>Strategic Decision Matrix</Text>
                              </View>
                              <ToggleSwitch
                                isOn={schedulePreferences.weeklyActions?.strategicDecision?.enabled}
                                onToggle={() => handleScheduleToggle('weeklyActions.strategicDecision.enabled')}
                                disabled={savingSchedule}
                              />
                            </View>
                          </View>
                        </>
                      )}

                      {/* Progress Reports */}
                      <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginTop: 32 }]}>
                        <Text style={{ fontSize: 20 }}>📊</Text> Progress Reports
                      </Text>
                      <View style={styles.toggleRow}>
                        <View style={styles.toggleInfo}>
                          <Text style={[styles.toggleTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Weekly Progress Review</Text>
                          <Text style={[styles.toggleDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Reminder to review your weekly progress report</Text>
                        </View>
                        <ToggleSwitch
                          isOn={schedulePreferences.progressReports?.weekly?.enabled}
                          onToggle={() => handleScheduleToggle('progressReports.weekly.enabled')}
                          disabled={savingSchedule}
                        />
                      </View>
                    </>
                  )}

                  {/* Save Button */}
                  <View style={styles.saveButtonContainer}>
                    <TouchableOpacity
                      onPress={saveScheduleSettings}
                      disabled={savingSchedule}
                      style={[styles.saveScheduleButton, { backgroundColor: palette.primary || '#FF4747' }]}
                    >
                      {savingSchedule ? (
                        <ActivityIndicator size="small" color="white" />
                      ) : (
                        <Text style={styles.saveScheduleButtonText}>Save & Sync Calendar</Text>
                      )}
                    </TouchableOpacity>
                  </View>

                  {/* Info Box */}
                  <View style={[styles.infoBox, { backgroundColor: palette.surface || 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                    <MaterialCommunityIcons name="calendar-sync" size={20} color={palette.primary || '#FF4747'} />
                    <Text style={[styles.infoText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                      Calendar events are automatically created for the next 3 months and sync when you complete tasks. Events will be marked as completed when you finish each activity.
                    </Text>
                  </View>
                </>
              ) : null}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingLeft: 48,
    paddingRight: 48,
    paddingTop: 160,
    paddingBottom: 40,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  frequencyButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  frequencyButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    minWidth: 70,
    alignItems: 'center',
  },
  frequencyButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '500',
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 16,
    gap: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  layoutRow: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 48,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainCol: { 
    flex: 1,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  headerBlock: { zIndex: 1000, paddingTop: 16, paddingBottom: 16 },
  headerTitle: { fontSize: 28, fontFamily: 'Chivo', fontWeight: '900', lineHeight: 32 },
  profileEditRow: {
    width: '100%',
    maxWidth: 900,
    paddingTop: 16,
    paddingBottom: 16,
    alignItems: 'flex-end',
    gap: 24,
    flexDirection: 'row',
  },
  avatarWrap: { width: 84, height: 84, position: 'relative' },
  bigAvatar: { width: 84, height: 84, borderRadius: 999, borderWidth: 1, borderColor: 'transparent' },
  avatarPlaceholder: {
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  avatarEditBtnContainer: {
    position: 'absolute',
    left: 56,
    top: 48,
    zIndex: 1000,
  },
  avatarEditBtn: {
    width: 36,
    height: 36,
    padding: 6,
    borderRadius: 99,
    alignItems: 'center',
    justifyContent: 'center'
  },
  avatarEditBtnContainerInner: {
    position: 'relative',
    zIndex: 1000,
  },
  photoMenu: {
    position: 'absolute',
    top: 90,
    left: 56,
    minWidth: 150,
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 4,
    elevation: 8,
    zIndex: 1002,
    ...Platform.select({
      web: {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
    }),
  },
  photoMenuItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  photoMenuText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '500',
    lineHeight: 20,
  },
  photoMenuDivider: {
    height: 1,
    marginVertical: 4,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  toggleInfo: {
    flex: 1,
    marginRight: 16,
  },
  toggleTitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 4,
  },
  toggleDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  sectionBlock: {
    paddingTop: 32,
    borderTopWidth: 1,
  },
  sectionTitle: {
    fontSize: 20,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 24,
  },
  dropdownOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
    backgroundColor: 'transparent',
  },
  usernameCol: { flex: 1, gap: 4 },
  label: { fontSize: 13, fontFamily: 'Archivo', fontWeight: '600', lineHeight: 16 },
  outlineField: {
    borderRadius: 22,
    justifyContent: 'center',
  },

  sectionBlock: {
    width: '100%',
    maxWidth: 900,
    paddingTop: 16,
    paddingBottom: 16,
    gap: 12,
    borderTopWidth: 1,
  },
  sectionTitle: { fontSize: 20, fontFamily: 'Chivo', fontWeight: '900', lineHeight: 24 },
  toggleRow: {
    alignSelf: 'stretch',
    paddingVertical: 12,
    backgroundColor: 'rgba(255, 255, 255, 0)',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 16,
    display: 'flex',
    flexDirection: 'row',
  },
  toggleInfo: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 4,
    display: 'flex',
  },
  toggleTitle: {
    alignSelf: 'stretch',
    fontSize: 17,
    fontFamily: 'Archivo',
    fontWeight: '400',
    lineHeight: 24,
  },
  toggleDescription: {
    alignSelf: 'stretch',
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '400',
    lineHeight: 20,
  },

  sectionHeader: { fontSize: 20, fontFamily: 'Chivo', fontWeight: '900', lineHeight: 24, marginBottom: 8 },
  inlineRow: { flexDirection: 'row', gap: 16 },
  inlineCol: { flex: 1, gap: 4 },
  smallLabel: { fontSize: 13, fontFamily: 'Archivo', fontWeight: '600', lineHeight: 16 },

  actionsRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 24 },
  outlineBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 22, borderWidth: 1 },
  outlineBtnText: { fontSize: 15, fontFamily: 'Archivo', fontWeight: '600', lineHeight: 20 },

  // Modal styles
  modalContentWrapper: {
    gap: 32,
    width: '100%',
  },
  modalMessage: {
    fontSize: 17,
    fontFamily: 'Archivo',
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
    color: 'inherit',
  },
  modalActions: {
    width: '100%',
  },
  modalSingleAction: {
    width: '100%',
    alignItems: 'center',
  },
  modalDualActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  modalCancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 100,
  },
  modalCancelButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    lineHeight: 20,
  },
  modalPrimaryButton: {
    minWidth: 100,
    alignSelf: 'stretch',
  },
  modalDeleteButton: {
    backgroundColor: '#EA806B',
  },
  frequencyButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  frequencyButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    minWidth: 70,
    alignItems: 'center',
  },
  frequencyButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '500',
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 16,
    gap: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  // Schedule Settings Styles
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  timePickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 16,
  },
  timeLabel: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '500',
    minWidth: 100,
  },
  timeButtons: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  timeButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 60,
    alignItems: 'center',
  },
  timeButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  dayOfWeekRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 16,
    flexWrap: 'wrap',
  },
  dayButtons: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  dayButton: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 44,
    alignItems: 'center',
  },
  dayButtonText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  weeklyActionsCard: {
    marginTop: 12,
    marginHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    gap: 12,
  },
  weeklyActionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  weeklyActionIcon: {
    fontSize: 24,
    width: 32,
    textAlign: 'center',
  },
  weeklyActionInfo: {
    flex: 1,
  },
  weeklyActionTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  saveButtonContainer: {
    marginTop: 32,
    paddingHorizontal: 16,
  },
  saveScheduleButton: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.85)',
        cursor: 'pointer',
      },
    }),
  },
  saveScheduleButtonText: {
    color: 'white',
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
});

export default ProfileScreen;

